import { getAuth } from 'firebase/auth'
import { useQuery } from '@apollo/client'
import { Link } from 'react-router-dom'
import { Avatar, Box, Stack, styled } from '@mui/material'
import { upperCase } from 'lodash'
import InviteButton from '../shared/InviteButton'
import logger from '../../../../utils/logger'
import useAlerts from '../../hooks/useAlerts'
import { BucketUsersDocument, Bucket } from '../../../../graphql/generated'

interface Props {
  bucket: Bucket
}

const AvatarsLink = styled(Link)(() => ({
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center'
}))

const AvatarStyled = styled(Avatar)(({ theme }) => ({
  width: '32px',
  height: '32px',
  background: theme.palette.primary.main,
  border: `1px solid #BDC0FF`,
  color: '#fff',
  fontSize: '12px',
  fontWeight: 600,
  marginLeft: '-8px',
  a: {
    textDecoration: 'none'
  }
}))

export default function InviteAvatars({ bucket }: Props) {
  const { createAlert } = useAlerts()
  const userId = getAuth().currentUser?.uid
  const bucketId = bucket.id

  const { data: bucketUsersData } = useQuery(BucketUsersDocument, {
    variables: { bucketId: bucketId! },
    onError: (e) => {
      e.graphQLErrors.forEach((error) => {
        createAlert(error.message, 'error', error)
      })

      if (!e.graphQLErrors) {
        logger.error(e)
        createAlert('We are having trouble retrieving your buckets. Please try again later.')
      }
    }
  })

  // Sort so logged-in user's avatar always appears
  const bucketUserCopy = bucketUsersData?.bucketUsers?.items.slice() || []
  const sortedBucketUsers = bucketUserCopy.sort((a, b) => {
    // Owner is always first
    if (a.id === bucket?.ownerId) return -1
    if (b.id === bucket?.ownerId) return 1

    // Current user is second
    if (a.id === userId) return -1
    if (b.id === userId) return 1

    return 0
  })

  return (
    <Stack flexDirection="row" sx={{ gap: '8px', height: '34px' }} alignItems="center">
      <AvatarsLink to={`/bucket/${bucketId}/manage`} data-testid="avatar-group-link">
        <Stack flexDirection="row">
          {sortedBucketUsers.slice(0, 3).map((user, idx) => {
            const { firstName, lastName } = user?.userProfile || {}
            const initials = firstName && lastName ? `${firstName[0]}${lastName[0]}` : ''
            return (
              <AvatarStyled
                key={user.id}
                data-testid={idx === 0 ? 'my-avatar' : 'other-avatar'}
                sx={{
                  background:
                    user.userRole === 'OWNER'
                      ? 'linear-gradient(154deg, #049f6e 15.29%, #73b255 84.04%)'
                      : 'linear-gradient(154deg, #1C47CB -15.36%, #02E39F 105.31%)'
                }}
              >
                {upperCase(initials || user?.userProfile?.name?.[0] || '')}
              </AvatarStyled>
            )
          })}
          {sortedBucketUsers.length > 3 && (
            <AvatarStyled data-testid="more-avatars" sx={{ background: 'linear-gradient(153deg, #5B62FF 0.71%, #C985FF 93.55%)' }}>{`+${
              sortedBucketUsers.length - 3
            }`}</AvatarStyled>
          )}
        </Stack>
      </AvatarsLink>
      <Box data-pendo="avatar-invite-btn">
        <InviteButton bucket={bucket} />
      </Box>
    </Stack>
  )
}
